import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { newsList } from '@/mocks/news';
import { getNewsById, getHotMedia } from '@/lib/media';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;

  if (!item) {
    return (
      <div className="mx-auto flex w-full max-w-[820px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <i className="ri-error-warning-line text-5xl text-foreground-300" />
        <h1 className="mt-4 text-xl font-bold text-foreground-950 font-heading">资讯暂不可用</h1>
        <p className="mt-2 text-sm text-foreground-500">该资讯可能已下线，请返回资讯页浏览其他内容</p>
        <Link
          to="/news"
          className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
        >
          返回资讯页
        </Link>
      </div>
    );
  }

  const others = newsList.filter((n) => n.id !== item.id).slice(0, 3);
  const hotMedia = getHotMedia(4);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视资讯', to: '/news' }, { label: item.title }]} />

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <div className="flex items-center gap-2 text-sm text-foreground-500">
            <span className="rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-700">{item.category}</span>
            <span>{item.date}</span>
          </div>
          <h1 className="mt-3 text-2xl font-bold leading-snug text-foreground-950 font-heading md:text-3xl">
            {item.title}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-foreground-600">{item.summary}</p>

          <div className="mt-6 aspect-video w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 八戒影院影视资讯`}
              className="h-full w-full object-top"
            />
          </div>

          <div className="mt-6 flex flex-col gap-4">
            {item.content.map((paragraph) => (
              <p key={paragraph.slice(0, 12)} className="text-sm leading-loose text-foreground-700 md:text-base">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-3 rounded-lg bg-background-100 p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <i className="ri-film-line text-xl" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-950">八戒影院 · 影视资讯</p>
              <p className="text-xs text-foreground-500">内容仅供演示，不代表真实影视信息</p>
            </div>
          </div>
        </article>

        <aside className="lg:col-span-1">
          <div className="sticky top-20 flex flex-col gap-6">
            <div className="rounded-lg bg-background-100 p-5">
              <h2 className="mb-4 text-base font-bold text-foreground-950 font-heading">相关资讯</h2>
              <div className="flex flex-col gap-4">
                {others.map((n) => (
                  <Link key={n.id} to={`/news/${n.id}`} className="group flex gap-3">
                    <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={n.cover}
                        alt={n.title}
                        title={`${n.title} - 八戒影院影视资讯`}
                        className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-accent-600">
                        {n.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{n.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-primary-950 p-5">
              <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-background-50 font-heading">
                <i className="ri-fire-line text-accent-400" />
                热门影视
              </h2>
              <div className="flex flex-col gap-3">
                {hotMedia.map((m, index) => (
                  <Link key={m.id} to={`/detail/${m.id}`} className="group flex items-center gap-3">
                    <span className="text-sm font-bold text-accent-400">{String(index + 1).padStart(2, '0')}</span>
                    <span className="line-clamp-1 flex-1 text-sm text-background-200 transition-colors group-hover:text-secondary-400">
                      {m.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}